import { useState } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import { useTranslation } from '@/hooks/useTranslation';
import { SimpleSelect } from '@/components/form/inputs/SimpleSelect';
import { Card } from '@/components/ui/card/Card';
import { Skeleton } from '@/components/ui/skeleton';
import { SmartLegend } from '../shared/SmartLegend';
import type { Tool } from '@/features/traceability/type/Tool';
import { COLORS } from '../../utils/palette';
import { shortToolId } from '../../utils/format';
import { formatToolHolderType, formatLubricationType } from '@/utils/format';

type DistributionDimension = 'material' | 'machine' | 'lubrication' | 'toolHolder';
type PerformanceMetric = 'duration' | 'pieces' | 'volume' | 'distance' | 'malround';

const DIMENSIONS: DistributionDimension[] = ['material', 'machine', 'lubrication', 'toolHolder'];
const METRICS: PerformanceMetric[] = ['duration', 'pieces', 'volume', 'distance', 'malround'];

type UsageDistributionProps = {
  tools: Tool[];
  isLoading: boolean;
};

export function UsageDistribution({ tools, isLoading }: UsageDistributionProps) {
  const { t } = useTranslation();
  const [selectedDimension, setSelectedDimension] = useState<DistributionDimension>('material');
  const [selectedMetric, setSelectedMetric] = useState<PerformanceMetric>('duration');

  // Calculate usage distribution based on selected dimension
  const getDistributionData = () => {
    const countMap = new Map<string, number>();
    const unknown = t('analytics.dashboard.complexCharts.defaults.unknown');

    tools.forEach((tool) => {
      tool.traces.forEach((trace) => {
        let key = '';
        switch (selectedDimension) {
          case 'material':
            key = trace.material?.name ?? unknown;
            break;
          case 'machine':
            key = trace.machine?.name ?? unknown;
            break;
          case 'lubrication':
            key = formatLubricationType(trace.lubType) || unknown;
            break;
          case 'toolHolder':
            key = formatToolHolderType(trace.holderType) || unknown;
            break;
        }
        countMap.set(key, (countMap.get(key) || 0) + 1);
      });
    });

    return Array.from(countMap.entries())
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  };

  // Calculate performance metrics for each unique tool
  const getPerformanceData = () => {
    return tools
      .map((tool) => {
        const metrics = tool.traces.reduce(
          (acc, trace) => {
            if (trace.totalMachiningDuration !== null) {
              acc.duration += trace.totalMachiningDuration;
            }
            if (trace.machinedPieces !== null) {
              acc.pieces += trace.machinedPieces;
            }
            if (trace.machinedVolume !== null) {
              acc.volume += trace.machinedVolume;
            }
            if (trace.machinedDistance !== null) {
              acc.distance += trace.machinedDistance;
            }
            if (trace.malround !== null) {
              acc.malround += trace.malround;
            }
            return acc;
          },
          { duration: 0, pieces: 0, volume: 0, distance: 0, malround: 0 }
        );

        return {
          toolId: shortToolId(tool.toolId),
          duration: metrics.duration,
          pieces: metrics.pieces,
          volume: metrics.volume,
          distance: metrics.distance,
          malround: metrics.malround / (tool.traces.length || 1),
          traceCount: tool.traces.length,
        };
      })
      .sort((a, b) => b[selectedMetric] - a[selectedMetric]);
  };

  const distributionData = getDistributionData();
  const performanceData = getPerformanceData();

  // Format metric value for display
  const formatMetricValue = (value: number, metric: PerformanceMetric) => {
    switch (metric) {
      case 'duration':
        if (value < 60) return `${value.toFixed(1)}s`;
        if (value < 3600) return `${(value / 60).toFixed(1)}min`;
        return `${(value / 3600).toFixed(1)}h`;
      case 'pieces':
        return value.toString();
      case 'volume':
        return `${value.toFixed(1)} cm³`;
      case 'distance':
        return `${value.toFixed(1)} mm`;
      case 'malround':
        return `${value.toFixed(1)} µm`;
    }
  };

  // Get metric label
  const getMetricLabel = (metric: PerformanceMetric) => {
    return t(`analytics.dashboard.globalPerformance.metrics.${metric}`);
  };

  // Get dimension label
  const getDimensionLabel = (dimension: DistributionDimension) => {
    return t(`analytics.dashboard.globalPerformance.dimensions.${dimension}`);
  };

  if (isLoading) {
    return (
      <section className="space-y-4">
        <div>
          <h2 className="section-title">{t('analytics.dashboard.globalPerformance.title')}</h2>
          <p className="text-sm text-muted-foreground">
            {t('analytics.dashboard.globalPerformance.description')}
          </p>
        </div>
        <div className="grid gap-6 lg:grid-cols-2">
          <Skeleton className="h-[400px] w-full rounded-xl" />
          <Skeleton className="h-[400px] w-full rounded-xl" />
        </div>
      </section>
    );
  }

  if (tools.length === 0) {
    return null;
  }

  return (
    <section className="space-y-4">
      <div>
        <h2 className="section-title">{t('analytics.dashboard.globalPerformance.title')}</h2>
        <p className="text-sm text-muted-foreground">
          {t('analytics.dashboard.globalPerformance.description')}
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Left: Usage Distribution Pie Chart */}
        <Card padding="lg" className="flex flex-col space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">
              {t('analytics.dashboard.globalPerformance.usageDistribution')}
            </h3>
            <SimpleSelect
              value={selectedDimension}
              onChange={(v) => v && setSelectedDimension(v as DistributionDimension)}
              options={DIMENSIONS.map((d) => ({ value: d, label: getDimensionLabel(d) }))}
              aria-label={t('analytics.dashboard.globalPerformance.usageDistribution')}
              className="w-auto min-w-[140px]"
            />
          </div>

          <ResponsiveContainer width="100%" height={320}>
            <PieChart>
              <Pie
                data={distributionData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={
                  distributionData.length <= 10
                    ? ({ name, percent }) => `${name}: ${((percent ?? 0) * 100).toFixed(0)}%`
                    : undefined
                }
                outerRadius={100}
                fill={COLORS[0]}
                dataKey="value"
              >
                {distributionData.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend
                content={(props: unknown) => {
                  const legendProps = props as {
                    payload?: Array<{ value?: string; color?: string }>;
                  };
                  return (
                    <SmartLegend
                      payload={legendProps.payload?.map((entry) => ({
                        value: entry.value || '',
                        color: entry.color || 'var(--foreground)',
                        label: String(entry.value || ''),
                      }))}
                      maxVisibleItems={10}
                      layout="horizontal"
                    />
                  );
                }}
              />
            </PieChart>
          </ResponsiveContainer>
          <div className="text-xs text-muted-foreground">
            {t('analytics.dashboard.globalPerformance.toolComparison', {
              count: performanceData.length,
            })}
          </div>
        </Card>

        {/* Right: Performance Comparison Bar Chart */}
        <Card padding="lg" className="flex flex-col space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">
              {t('analytics.dashboard.globalPerformance.performanceComparison')}
            </h3>
            <SimpleSelect
              value={selectedMetric}
              onChange={(v) => v && setSelectedMetric(v as PerformanceMetric)}
              options={METRICS.map((m) => ({ value: m, label: getMetricLabel(m) }))}
              aria-label={t('analytics.dashboard.globalPerformance.performanceComparison')}
              className="w-auto min-w-[140px]"
            />
          </div>

          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={performanceData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="toolId"
                angle={-45}
                textAnchor="end"
                height={80}
                tick={{ fontSize: 12 }}
              />
              <YAxis
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => {
                  if (selectedMetric === 'duration' && value >= 3600) {
                    return `${(value / 3600).toFixed(0)}h`;
                  }
                  if (selectedMetric === 'duration' && value >= 60) {
                    return `${(value / 60).toFixed(0)}m`;
                  }
                  return value.toString();
                }}
              />
              <Tooltip
                formatter={(value) => formatMetricValue(value as number, selectedMetric)}
                labelFormatter={(label) =>
                  `${t('analytics.dashboard.complexCharts.tooltip.tool')}: ${label}`
                }
              />
              <Bar dataKey={selectedMetric} fill="var(--primary)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>

          <div className="text-xs text-muted-foreground">
            {t('analytics.dashboard.globalPerformance.toolComparison', {
              count: performanceData.length,
            })}
          </div>
        </Card>
      </div>
    </section>
  );
}
